import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { getCurrentId, getSessionId } from '@/services/correlation';
import { env } from '@/utils/env';
import { TELEMETRY_COMPONENT, TELEMETRY_SERVICE, EVENT_TYPES, EventType } from '@/constants';
import { getDeviceInfo } from '@/services/deviceInfo';

let appInsights: ApplicationInsights | null = null;
let isInitialized = false;

export function initializeTelemetry(): void {
  const { appInsightsKey } = env();

  if (!appInsightsKey) {
    if (import.meta.env.DEV) {
      console.warn('Telemetry: App Insights key not configured, telemetry disabled');
    }
    return;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: appInsightsKey,
        enableAutoRouteTracking: false,
        disableFetchTracking: false,
        enableCorsCorrelation: true,
      },
    });
    appInsights.loadAppInsights();
    isInitialized = true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('Telemetry: Failed to initialize App Insights', error);
    }
  }
}

export function getAppInsightsInstance(): ApplicationInsights | null {
  return appInsights;
}

function addContext(properties?: Record<string, string>): Record<string, string> {
  const deviceInfo = getDeviceInfo();
  const { envName } = env();

  return {
    ...properties,
    correlationId: getCurrentId(),
    sessionId: getSessionId(),
    ComponentName: TELEMETRY_COMPONENT,
    EnvironmentName: envName,
    Service: TELEMETRY_SERVICE.NAME,
    ServiceLine: TELEMETRY_SERVICE.LINE,
    ServiceOffering: TELEMETRY_SERVICE.OFFERING,
    DeviceName: deviceInfo.DeviceName,
    DeviceLocation: deviceInfo.DeviceLocation,
  };
}

function formatMessage(
  component: string,
  action: string,
  resource: string,
  status: string
): string {
  return `${component} - ${action} - ${resource} - ${status}`;
}

export const telemetry = {
  trackPageView(
    componentName: string,
    pageName: string,
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const name = `${componentName}.${EVENT_TYPES.PAGE_VIEW}`;
    appInsights.trackPageView({
      name,
      properties: addContext({
        pageName,
        message: formatMessage(componentName, EVENT_TYPES.PAGE_VIEW, pageName, 'Loaded'),
        ...properties,
      }),
    });
  },

  trackEvent(
    componentName: string,
    eventType: EventType,
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const name = `${componentName}.${eventType}`;
    appInsights.trackEvent({ name, properties: addContext(properties) });
  },

  trackButtonClick(
    componentName: string,
    buttonName: string,
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const name = `${componentName}.${EVENT_TYPES.BUTTON_CLICK}`;
    appInsights.trackEvent({
      name,
      properties: addContext({
        buttonName,
        message: formatMessage(componentName, EVENT_TYPES.BUTTON_CLICK, buttonName, 'Clicked'),
        ...properties,
      }),
    });
  },

  trackSuccess(
    componentName: string,
    operationName: string,
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const name = `${componentName}.${EVENT_TYPES.SUCCESS}`;
    appInsights.trackEvent({
      name,
      properties: addContext({
        operationName,
        message: formatMessage(componentName, operationName, '', 'Success'),
        ...properties,
      }),
    });
  },

  trackFailure(
    componentName: string,
    operationName: string,
    error?: string,
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const name = `${componentName}.${EVENT_TYPES.FAILURE}`;
    appInsights.trackEvent({
      name,
      properties: addContext({
        operationName,
        error: error ?? '',
        message: formatMessage(componentName, operationName, error ?? '', 'Failed'),
        ...properties,
      }),
    });
  },

  trackDependency(
    endpoint: string,
    method: string,
    statusCode: number,
    durationMs: number,
    success: boolean,
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    appInsights.trackDependencyData({
      id: getCurrentId(),
      name: `${method} ${endpoint}`,
      duration: durationMs,
      success,
      responseCode: statusCode,
      type: 'HTTP',
      target: endpoint,
      properties: addContext({
        endpoint,
        method,
        statusCode: String(statusCode),
        durationMs: String(durationMs),
        message: formatMessage('EGEM-UI.Api', method, endpoint, success ? 'Completed' : 'Failed'),
        ...properties,
      }),
    });
  },

  trackError(
    componentName: string,
    operationName: string,
    error: string,
    errorCategory: 'network' | 'validation' | 'server' | 'unknown',
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const name = `${componentName}.${EVENT_TYPES.ERROR}`;
    appInsights.trackEvent({
      name,
      properties: addContext({
        operationName,
        error,
        errorCategory,
        message: formatMessage(componentName, operationName, error, 'Error'),
        ...properties,
      }),
    });
  },

  trackOperationStart(
    componentName: string,
    operationType: 'Api' | 'Function',
    operationName: string,
    properties?: Record<string, string>
  ): number {
    const startTime = Date.now();
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return startTime;
    const name = `${componentName}.${operationType}.${operationName}.${EVENT_TYPES.START}`;
    appInsights.trackEvent({ name, properties: addContext({ operationName, ...properties }) });
    return startTime;
  },

  trackOperationEnd(
    componentName: string,
    operationType: 'Api' | 'Function',
    operationName: string,
    startTime: number,
    status: 'Success' | 'Failure',
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const durationMs = String(Date.now() - startTime);
    const name = `${componentName}.${operationType}.${operationName}.${EVENT_TYPES.END}`;
    appInsights.trackEvent({
      name,
      properties: addContext({ operationName, status, durationMs, ...properties }),
    });
  },

  trackException(error: Error, componentName?: string, properties?: Record<string, string>): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const contextProps = componentName ? { componentName, ...properties } : properties;
    appInsights.trackException({ exception: error, properties: addContext(contextProps) });
  },

  trackWarning(
    componentName: string,
    operationName: string,
    message: string,
    properties?: Record<string, string>
  ): void {
    /* v8 ignore next */
    if (!isInitialized || !appInsights) return;
    const name = `${componentName}.Warning`;
    appInsights.trackEvent({
      name,
      properties: addContext({
        operationName,
        message,
        severity: 'Warning',
        ...properties,
      }),
    });
  },
};
